import React from 'react';
import Box from '../../components/Common/Box';

interface LeaderboardEntry {
    rank: number | null;
    pseudonym: string | null;
    twr_performance: number;
    calculated_at: string;
    entity_type?: string;
    entity_id?: string;
    is_benchmark?: boolean;
}

interface DesktopLeaderboardProps {
    activeTab: 'LOCAL' | 'GLOBAL';
    setActiveTab: (tab: 'LOCAL' | 'GLOBAL') => void;
    period: 'TODAY' | 'WEEKLY' | 'MONTHLY' | 'LAST_MONTH' | 'YTD';
    setPeriod: (period: 'TODAY' | 'WEEKLY' | 'MONTHLY' | 'LAST_MONTH' | 'YTD') => void;
    loading: boolean;
    localData: any[];
    globalData: LeaderboardEntry[];
    myRank: { rank: number | null; twr_performance: number | null; pseudonym: string | null; exclusion: string | null } | null;
    hasOptedIn: boolean;
    user: any;
}

const DesktopLeaderboard: React.FC<DesktopLeaderboardProps> = ({
    activeTab, setActiveTab, period, setPeriod, loading, localData, globalData, myRank, hasOptedIn, user
}) => {
    return (
        <div style={{ maxWidth: '1200px', margin: '40px auto', padding: '0 20px' }}>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', marginBottom: '30px' }}>
                <button
                    onClick={() => setActiveTab('LOCAL')}
                    style={{
                        padding: '16px 32px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'LOCAL' ? '3px solid #1e40af' : '3px solid transparent',
                        color: activeTab === 'LOCAL' ? '#1e40af' : 'var(--text-muted)',
                        fontWeight: activeTab === 'LOCAL' ? '700' : '500',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'all 0.2s'
                    }}
                >
                    Comparatif Privé
                </button>
                <button
                    onClick={() => setActiveTab('GLOBAL')}
                    style={{
                        padding: '16px 32px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderBottom: activeTab === 'GLOBAL' ? '3px solid #1e40af' : '3px solid transparent',
                        color: activeTab === 'GLOBAL' ? '#1e40af' : 'var(--text-muted)',
                        fontWeight: activeTab === 'GLOBAL' ? '700' : '500',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'all 0.2s'
                    }}
                >
                    Classement Général
                </button>
            </div>

            {/* Header Card & Filters */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '30px',
                padding: '24px 32px',
                background: 'var(--bg-box)',
                borderRadius: '16px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.02)',
                border: '1px solid var(--border-color)'
            }}>
                <div>
                    {activeTab === 'LOCAL' ? (
                        <>
                            <h2 style={{ margin: 0, color: 'var(--text-heading)', fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '28px' }}>🏅</span> Comparatif Privé
                            </h2>
                            <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '15px' }}>
                                Mesurez vos performances face aux indices majeurs du marché et à notre IA (Nia).
                            </p>
                        </>
                    ) : (
                        <>
                            <h2 style={{ margin: 0, color: 'var(--text-heading)', fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '28px' }}>🌍</span> Classement Général
                            </h2>
                            <p style={{ margin: '8px 0 0 0', color: 'var(--text-muted)', fontSize: '15px' }}>
                                Faites la course en tête ! Comparez vos rendements avec ceux de la communauté Kanea.
                            </p>
                        </>
                    )}
                </div>

                <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value as any)}
                    style={{
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-input)',
                        fontSize: '15px',
                        fontWeight: '600',
                        color: 'var(--text-main)',
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                >
                    <>
                        <option value="TODAY">Aujourd'hui</option>
                        <option value="WEEKLY">Cette Semaine</option>
                        <option value="MONTHLY">Ce Mois-ci</option>
                        <option value="LAST_MONTH">Mois dernier</option>
                        <option value="YTD">Depuis le 1er Janvier (YTD)</option>
                    </>
                </select>
            </div>

            {!hasOptedIn && activeTab === 'GLOBAL' && (
                <div style={{ backgroundColor: '#fffaf0', border: '1px solid #feebc8', color: '#dd6b20', padding: '20px', borderRadius: '12px', marginBottom: '30px', fontSize: '15px' }}>
                    <strong>Attention :</strong> Vous n'êtes pas encore inscrit au classement public.
                    Allez dans votre <strong>Profil</strong> pour définir un pseudonyme et participer à la compétition.
                </div>
            )}

            {!loading && myRank?.exclusion && (
                <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '20px', borderRadius: '12px', marginBottom: '30px', fontSize: '15px', lineHeight: '1.5' }}>
                    {myRank.exclusion}
                </div>
            )}

            <Box>
                <div style={{ padding: '0' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)', fontSize: '16px' }}>Mise à jour du classement...</div>
                    ) : activeTab === 'LOCAL' ? (
                        localData.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)', fontSize: '16px' }}>
                                Aucune donnée disponible pour cette période.
                            </div>
                        ) : (() => {
                            // Sort by performance descending for ranking
                            const sorted = [...localData].sort((a, b) => b.twr_performance - a.twr_performance);
                            return (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'var(--bg-box)' }}>
                                            <th style={{ padding: '20px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', width: '80px', borderTopLeftRadius: '12px' }}>Rang</th>
                                            <th style={{ padding: '20px 24px', textAlign: 'left', color: 'var(--text-muted)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Participant</th>
                                            <th style={{ padding: '20px 24px', textAlign: 'right', color: 'var(--text-muted)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', borderTopRightRadius: '12px' }}>Performance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sorted.map((item, index) => {
                                            const isUser = item.entity_type === 'USER';
                                            const isAI = item.entity_type === 'AI';
                                            const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;
                                            const perf = item.twr_performance;

                                            // Dynamic styling based on row type
                                            let bgColor = index % 2 === 0 ? 'transparent' : 'var(--bg-hover)';
                                            if (isUser) bgColor = 'var(--bg-input)'; // Better for dark mode
                                            let borderColor = 'var(--border-color)';

                                            return (
                                                <tr key={index} style={{
                                                    backgroundColor: bgColor,
                                                    transition: 'all 0.2s ease',
                                                    borderBottom: `1px solid ${borderColor}`
                                                }}>
                                                    <td style={{
                                                        padding: '24px',
                                                        textAlign: 'center',
                                                        fontSize: medal ? '28px' : '18px',
                                                        fontWeight: '800',
                                                        color: medal ? 'var(--text-main)' : 'var(--text-muted)'
                                                    }}>
                                                        {medal || <span style={{ opacity: 0.7 }}>#{index + 1}</span>}
                                                    </td>
                                                    <td style={{
                                                        padding: '24px',
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            {isUser && <span style={{ fontSize: '24px' }}>🧑‍💼</span>}
                                                            {isAI && <span style={{ fontSize: '24px' }}>🤖</span>}
                                                            {!isUser && !isAI && <span style={{ fontSize: '24px' }}>📈</span>}
                                                            <div>
                                                                <div style={{
                                                                    fontWeight: isUser ? '800' : '600',
                                                                    color: isUser ? '#3b82f6' : 'var(--text-heading)',
                                                                    fontSize: isUser ? '18px' : '17px'
                                                                }}>
                                                                    {isUser ? 'Mon Portefeuille' : item.entity_id}
                                                                </div>
                                                                {isUser && <div style={{ fontSize: '13px', color: '#3b82f6', marginTop: '4px', fontWeight: '500' }}>Vous</div>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{
                                                        padding: '24px',
                                                        textAlign: 'right',
                                                        fontWeight: '800',
                                                        fontSize: '20px',
                                                        color: perf >= 0 ? '#16a34a' : '#dc2626',
                                                        letterSpacing: '-0.02em'
                                                    }}>
                                                        <span style={{
                                                            backgroundColor: perf >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                                                            padding: '6px 16px',
                                                            borderRadius: '8px',
                                                            display: 'inline-block'
                                                        }}>
                                                            {perf > 0 ? '+' : ''}{perf.toFixed(2)}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            );
                        })()
                    ) : (
                        globalData.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)', fontSize: '16px' }}>
                                Le classement est en cours de calcul ou il n'y a aucun participant pour le moment.
                            </div>
                        ) : (
                            <>
                            <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border-color)', fontSize: '13px', color: 'var(--text-muted)' }}>
                                🏆 Top {Math.min(globalData.length, 5)} traders — {globalData.length} participant{globalData.length > 1 ? 's' : ''}
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'var(--bg-box)' }}>
                                        <th style={{ padding: '20px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', width: '80px', borderTopLeftRadius: '12px' }}>Rang</th>
                                        <th style={{ padding: '20px 24px', textAlign: 'left', color: 'var(--text-muted)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Trader</th>
                                        <th style={{ padding: '20px 24px', textAlign: 'right', color: 'var(--text-muted)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.1em', borderTopRightRadius: '12px' }}>Performance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {globalData.slice(0, 5).map((item, index) => {
                                        const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;
                                        const perf = item.twr_performance;
                                        const isMe = item.pseudonym === user?.social_profile?.pseudonym;
                                        const displayName = item.pseudonym && item.pseudonym.length > 40 ? item.pseudonym.slice(0, 40) + '…' : (item.pseudonym || 'Utilisateur Anonyme');

                                        // Dynamic styling based on row type
                                        let bgColor = index % 2 === 0 ? 'transparent' : 'var(--bg-hover)';
                                        if (isMe) bgColor = 'var(--bg-input)'; // Light blue for user

                                        return (
                                            <tr key={index} style={{
                                                backgroundColor: bgColor,
                                                transition: 'all 0.2s ease',
                                                borderBottom: '1px solid var(--border-color)'
                                            }}>
                                                <td style={{
                                                    padding: '24px',
                                                    textAlign: 'center',
                                                    fontSize: medal ? '28px' : '18px',
                                                    fontWeight: '800',
                                                    color: medal ? 'var(--text-main)' : 'var(--text-muted)'
                                                }}>
                                                    {medal || <span style={{ opacity: 0.7 }}>#{index + 1}</span>}
                                                </td>
                                                <td style={{
                                                    padding: '24px',
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ fontSize: '24px' }}>
                                                            {item.is_benchmark ? '📈' : '🧑‍🎓'}
                                                        </span>
                                                        <div>
                                                            <div style={{
                                                                fontWeight: isMe ? '800' : '600',
                                                                color: isMe ? '#3b82f6' : 'var(--text-heading)',
                                                                fontSize: isMe ? '18px' : '17px',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'anywhere'
                                                            }}>
                                                                {displayName}
                                                            </div>
                                                            {isMe && <div style={{ fontSize: '13px', color: '#3b82f6', marginTop: '4px', fontWeight: '500' }}>Vous</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{
                                                    padding: '24px',
                                                    textAlign: 'right',
                                                    fontWeight: '800',
                                                    fontSize: '20px',
                                                    color: perf >= 0 ? '#16a34a' : '#dc2626',
                                                    letterSpacing: '-0.02em'
                                                }}>
                                                    <span style={{
                                                        backgroundColor: perf >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)',
                                                        padding: '6px 16px',
                                                        borderRadius: '8px',
                                                        display: 'inline-block'
                                                    }}>
                                                        {perf > 0 ? '+' : ''}{perf.toFixed(2)}%
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {/* Rang du user s'il est hors top 10 */}
                            {(() => {
                                const isMeInTop5 = globalData.slice(0, 5).some(item => item.pseudonym === user?.social_profile?.pseudonym);
                                if (!isMeInTop5 && myRank && myRank.rank) {
                                    const perf = myRank.twr_performance!;
                                    const displayName = myRank.pseudonym && myRank.pseudonym.length > 40 ? myRank.pseudonym.slice(0, 40) + '…' : (myRank.pseudonym || 'Utilisateur Anonyme');
                                    
                                    // Calculate virtual rank including index
                                    const indexItem = globalData.find(item => item.is_benchmark);
                                    const virtualRank = (indexItem && indexItem.twr_performance > (myRank.twr_performance || 0))
                                        ? myRank.rank + 1
                                        : myRank.rank;

                                    return (
                                        <>
                                            <div style={{ textAlign: 'center', padding: '8px', color: 'var(--text-muted)', fontSize: '13px', letterSpacing: '0.2em' }}>•••</div>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <tbody>
                                                    <tr style={{ backgroundColor: 'var(--bg-input)', borderTop: '2px dashed var(--border-color)' }}>
                                                        <td style={{ padding: '20px 24px', textAlign: 'center', width: '80px', fontSize: '18px', fontWeight: '800', color: 'var(--text-muted)' }}>
                                                            <span style={{ opacity: 0.7 }}>#{virtualRank}</span>
                                                        </td>
                                                        <td style={{ padding: '20px 24px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                <span style={{ fontSize: '24px' }}>🧑‍🎓</span>
                                                                <div>
                                                                    <div style={{ fontWeight: '800', color: '#3b82f6', fontSize: '18px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{displayName}</div>
                                                                    <div style={{ fontSize: '13px', color: '#3b82f6', marginTop: '4px', fontWeight: '500' }}>Vous</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '20px 24px', textAlign: 'right', fontWeight: '800', fontSize: '20px', color: perf >= 0 ? '#16a34a' : '#dc2626', letterSpacing: '-0.02em' }}>
                                                            <span style={{ backgroundColor: perf >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(220, 38, 38, 0.1)', padding: '6px 16px', borderRadius: '8px', display: 'inline-block' }}>
                                                                {perf > 0 ? '+' : ''}{perf.toFixed(2)}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </>
                                    );
                                }
                                return null;
                            })()}
                            </>
                        )
                    )}
                </div>
            </Box>
        </div >
    );
};

export default DesktopLeaderboard;
